import { useRef, useState } from "react";

import { Link } from "react-router-dom";
import { useXFlowPartnerContext } from "../../context/XFlowPartners";
import { SearchIcon } from "../Icons";

export default function PartnersList() {
  const { data: allPartners, loading } = useXFlowPartnerContext();
  const [query, setQuery] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);
  const h2Ref = useRef<HTMLHeadingElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleFocus = () => {
    if (containerRef.current) {
      containerRef.current.classList.remove("w-[8rem]");
      containerRef.current.classList.add("w-full");
    }

    if (h2Ref.current) {
      h2Ref.current.classList.add("opacity-0", "w-0", "overflow-hidden");
      h2Ref.current.classList.remove("opacity-100");
    }
  };

  const handleBlur = () => {
    if (containerRef.current) {
      containerRef.current.classList.remove("w-full");
      containerRef.current.classList.add("w-[8rem]");
    }

    if (h2Ref.current) {
      h2Ref.current.classList.remove("opacity-0", "w-0", "overflow-hidden");
      h2Ref.current.classList.add("opacity-100");
    }
  };

  const filteredPartner = allPartners?.filter((el) =>
    el.business_details.legal_name
      .toLocaleLowerCase()
      .includes(query.toLocaleLowerCase())
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const getHighlightedText = (text: string, highlight: string) => {
    const parts = text.split(new RegExp(`(${highlight})`, "gi"));

    return (
      <span>
        {parts.map((part, index) => {
          return part.toLocaleLowerCase() === highlight.toLocaleLowerCase() ? (
            <b key={index} className="text-custom-blue font-semibold">
              {part}
            </b>
          ) : (
            part
          );
        })}
      </span>
    );
  };

  return (
    <div className="border-l p-4">
      <div className="flex items-center justify-between mb-4">
        <h2
          ref={h2Ref}
          className="font-medium transition-all duration-300 opacity-100"
        >
          Partners
        </h2>

        <div
          ref={containerRef}
          className="relative w-[8rem] transition-all duration-300 ease-in-out"
        >
          <input
            ref={inputRef}
            type="search"
            className="w-full p-[8px] pl-8 border-[0.66px] border-[#d0d5dd] rounded-lg placeholder:text-[#344054] transition-all duration-300 focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[rgba(21,93,233,0.2)]"
            placeholder="Search..."
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <SearchIcon className="size-6 absolute top-2 left-1 text-gray-500" />
        </div>
      </div>

      <div className="space-y-2 py-2 h-[550px] overflow-hidden overflow-y-scroll">
        {filteredPartner && filteredPartner?.length > 0 ? (
          filteredPartner.map((el) => (
            <div
              key={el.id}
              className="space-y-2 border-b p-2 flex items-center gap-2"
            >
              <div className="bg-gray-400 size-8 rounded-full text-white text-lg flex items-center justify-center">
                {el.nickname.at(0)?.toLocaleUpperCase()}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-[14px] flex items-center gap-1">
                    <Link
                      to={`/simple-payment/partner?partner_id=${el.id}`}
                      className="hover:underline"
                    >
                      {el.nickname}{" "}
                    </Link>
                    <span
                      className={`text-[10px] ${
                        el.status === "verifying" && "text-orange-400"
                      } ${el.status === "activated" && "text-green-400"}`}
                    >
                      {el.status}
                    </span>
                  </h3>
                </div>

                <div className="text-sm">
                  <p className="text-[10px] text-gray-500">
                    {getHighlightedText(el.business_details.legal_name, query)}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : loading ? (
          <div className="py-4 space-y-5">
            {[...Array(3)].map((_, i) => (
              <PartnerSkeleton key={i} />
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">No data found</p>
        )}
      </div>
    </div>
  );
}

export function PartnerSkeleton() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-4 w-[150px] bg-slate-300 rounded"></div>

      <div className="w-[300px] space-y-1">
        <div className="h-2 bg-slate-300 rounded"></div>
        <div className="h-2 bg-slate-300 rounded"></div>
      </div>

      <div className="h-2 w-[200px] bg-slate-300 rounded"></div>
    </div>
  );
}
